import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import IconPicker from "./IconPicker";
import * as Icons from "lucide-react";
import { Save, X, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { cn } from "../lib/utils";

const BADGE_OPTIONS = [null, "New", "Beta", "Roadmap", "On Deck", "Future"];
const CATEGORY_OPTIONS = [
  { value: "compliance", label: "Compliance" },
  { value: "system_health", label: "System Health" },
];

const DEFAULT = {
  name: "",
  description: "",
  icon: "LayoutGrid",
  icon_image: null,
  url: "https://",
  category: "system_health",
  display_order: 100,
  show_on_dashboard: true,
  allow_iframe: false,
  internal_only: false,
  ip_restrictions: [],
  roles: [],
  status_badge: null,
  lifecycle: "active",
  metric_label: "",
  metric_value: "",
  metric_trend: "flat",
};

export default function EditAppModal({ app, onClose, onSaved, onDelete }) {
  const { toast } = useToast();
  const [form, setForm] = useState(DEFAULT);
  const [ipText, setIpText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const seed = app ? { ...DEFAULT, ...app } : DEFAULT;
    setForm(seed);
    setIpText((seed.ip_restrictions || []).join("\n"));
    setConfirmDel(false);
  }, [app]);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast({ title: "Image too large", description: "Max 512 KB", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => patch({ icon_image: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        display_order: Number(form.display_order) || 100,
        ip_restrictions: ipText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (app?.id) {
        await api.put(`/admin/apps/${app.id}`, payload);
      } else {
        await api.post("/admin/apps", payload);
      }
      toast({ title: app?.id ? "App updated" : "App created" });
      onSaved?.();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const IconPreview = Icons[form.icon] || Icons.LayoutGrid;

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto tabler-scroll">
        <DialogHeader>
          <DialogTitle>{app?.id ? "Edit app" : "Register app"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="e.g. NinjaOne" className="mt-1" autoFocus />
          </div>
          <div>
            <Label>URL</Label>
            <Input value={form.url} onChange={(e) => patch({ url: e.target.value })} placeholder="https://…" className="mt-1" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => patch({ description: e.target.value })} rows={2} className="mt-1" />
          </div>

          <div>
            <Label>App icon (square image)</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-14 h-14 rounded-lg border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                {form.icon_image ? (
                  <img src={form.icon_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <IconPreview className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-[13px] hover:border-primary/40">
                <Upload className="w-4 h-4" />
                <span>{form.icon_image ? "Replace image" : "Choose file"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </label>
              {form.icon_image && (
                <button onClick={() => patch({ icon_image: null })} className="text-[12px] text-destructive hover:underline">Remove image</button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Square PNG/SVG under 512 KB. Falls back to the lucide icon below.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Fallback icon</Label>
              <div className="mt-1">
                <IconPicker
                  value={form.icon || "LayoutGrid"}
                  onChange={(name) => patch({ icon: name })}
                />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Used when no image is uploaded.</p>
            </div>
            <div>
              <Label>Display order</Label>
              <Input type="number" value={form.display_order} onChange={(e) => patch({ display_order: e.target.value })} className="mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => patch({ category: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lifecycle</Label>
              <Select value={form.lifecycle} onValueChange={(v) => patch({ lifecycle: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-[13.5px]">Allow iframe embed</div>
                <div className="text-[12px] text-muted-foreground">Off = open in new tab only.</div>
              </div>
              <Switch checked={form.allow_iframe} onCheckedChange={(v) => patch({ allow_iframe: v })} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-[13.5px]">Show on dashboard</div>
                <div className="text-[12px] text-muted-foreground">Off = hidden from main dashboard app grid (still on Apps page).</div>
              </div>
              <Switch checked={form.show_on_dashboard} onCheckedChange={(v) => patch({ show_on_dashboard: v })} />
            </div>
          </div>

          <div>
            <Label>Status badge (only one)</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {BADGE_OPTIONS.map((b) => (
                <button key={b || "none"} type="button" onClick={() => patch({ status_badge: b })}
                  className={cn("px-3 h-8 rounded-full border text-[12px] font-medium",
                    (form.status_badge || null) === b ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground")}>
                  {b || "None"}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-[13.5px]">Internal only (IP-locked)</div>
                <div className="text-[12px] text-muted-foreground">Restrict launch to allow-listed IPs below.</div>
              </div>
              <Switch checked={form.internal_only} onCheckedChange={(v) => patch({ internal_only: v })} />
            </div>
            {form.internal_only && (
              <div>
                <Label className="text-[12px]">Restrict by IP address (one IP or CIDR per line)</Label>
                <Textarea value={ipText} onChange={(e) => setIpText(e.target.value)} rows={3} className="mt-1 font-mono text-[12px]" placeholder="203.0.113.5&#10;10.0.0.0/24" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Metric label</Label>
              <Input value={form.metric_label || ""} onChange={(e) => patch({ metric_label: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Metric value</Label>
              <Input value={form.metric_value || ""} onChange={(e) => patch({ metric_value: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Trend</Label>
              <Select value={form.metric_trend || "flat"} onValueChange={(v) => patch({ metric_trend: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="up">Up</SelectItem>
                  <SelectItem value="down">Down</SelectItem>
                  <SelectItem value="flat">Flat</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex items-center gap-2">
            {onDelete && !confirmDel && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDel(true)}
                className="text-destructive hover:text-destructive gap-1.5"
                data-testid="app-delete-btn"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            )}
            {onDelete && confirmDel && (
              <>
                <span className="text-[12.5px] text-destructive font-medium">Delete this app?</span>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleting}
                  onClick={async () => {
                    setDeleting(true);
                    try { await onDelete(); }
                    finally { setDeleting(false); setConfirmDel(false); }
                  }}
                  className="h-8 gap-1.5"
                  data-testid="app-delete-confirm-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> {deleting ? "Deleting…" : "Yes, delete"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setConfirmDel(false)} className="h-8" data-testid="app-delete-cancel-btn">
                  Cancel
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name} className="gap-1.5"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
